import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  Announcement03,
  ArrowLeft,
  ArrowRight,
  BankNote01,
  ChevronLeft,
  Colors,
  DotsVertical,
  Eye,
  FilePlus02,
  Globe05,
  MessageTextSquare02,
  RefreshCcw05,
  SearchMd,
  Target04,
  User01,
  UserSquare,
  Users02,
  UserX01,
  XCircle,
} from "@untitledui/icons";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useCandidate } from "@/api/candidates";
import { useTargetCollaborationDetail, useTargetCollaborationGroupDetail } from "@/api/collaborations";
import { useCreator } from "@/api/creator";
import {
  type MerchantRecommendedCreator,
  type RecommendationsFilters,
  type RecommendedCreator,
  useCollaborationRecommendationRowsFetcher,
  useCollaborationRecommendations,
  useMerchantRecommendationRowsFetcher,
  useMerchantRecommendations,
  useOptimisticallyRemoveCollaborationRecommendations,
  usePassMerchantRecommendation,
  usePassRecommendation,
  useRecordFetchAction,
  useRecordInviteActionForGroup,
  useRecordMerchantInviteAction,
} from "@/api/recommendations";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { QuickInviteModal, shouldShowQuickInviteModal } from "@/components/assurance/quick-invite-modal";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { ProgressBarBase } from "@/components/base/progress-indicators/progress-indicators";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { RemoveCreatorFromCollabModal } from "@/components/remove-creator-from-collab-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { VideoDetails } from "@/components/video-details";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useCurrency } from "@/hooks/use-currency";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { useMerchant } from "@/providers/MerchantProvider";
import { success } from "@/providers/ToastProvider";
import { candidateExplorerRoute, creatorExplorerRoute } from "@/router";
import { cx } from "@/utils/cx";
import { compactNumberFormatter } from "@/utils/formatNumber";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { CandidateClassificationCard } from "../creators/candidate-classification-card";
import { CandidateDetails } from "../creators/candidate-details";
import { ClassificationCard } from "../creators/classification-card";
import { CreatorDetails } from "../creators/creator-details";

// Union type for recommendations that works in both collaboration and explorer modes
type UnifiedRecommendedCreator = RecommendedCreator | MerchantRecommendedCreator;

const BATCH_SIZE = 400;
const PASS_CONCURRENCY = 10;

async function runInBatches<T>(items: T[], size: number, task: (item: T) => Promise<unknown>) {
  for (let i = 0; i < items.length; i += size) await Promise.all(items.slice(i, i + size).map(task));
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numFormatter = new Intl.NumberFormat("en-US", { notation: "compact", compactDisplay: "short" });

type SortColumn = "name" | "score";
type SortDirection = "asc" | "desc";

const SortIcon = ({ active, direction }: { active: boolean; direction: SortDirection }) => (
  <svg
    aria-hidden="true"
    className={cx(
      "w-3.5 h-3.5 transition-transform",
      active ? "text-fg-secondary" : "text-fg-quaternary",
      direction === "asc" && active ? "rotate-180" : "",
    )}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 3.5v9M4.5 9.5l3.5 3.5 3.5-3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Shared helpers ───────────────────────────────────────────────────────────

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2 text-sm font-medium text-secondary pb-2 border-b border-border-secondary">
    {children}
  </div>
);

// ─── Shared profile layout helpers ───────────────────────────────────────────

const ProfileHeader = ({
  creator,
  categorySubtitle,
  description,
  onOpenProfile,
}: {
  creator: UnifiedRecommendedCreator;
  categorySubtitle: string;
  description?: string | null;
  onOpenProfile: (id: string, isCreator?: boolean) => void;
}) => {
  const profileId = creator.isCreator && creator.creatorId ? creator.creatorId : creator.tiktokCreatorOpenId;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-5 items-start">
        {creator.avatar ? (
          <img
            src={creator.avatar}
            alt={creator.nickname ?? creator.username}
            className="w-20 h-20 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-bg-quaternary flex items-center justify-center flex-shrink-0">
            <User01 className="w-8 h-8 text-fg-quaternary" />
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <button
              type="button"
              onClick={() => onOpenProfile(profileId, creator.isCreator)}
              className="text-xl font-semibold text-primary underline decoration-dotted underline-offset-4 hover:decoration-solid text-left"
            >
              {creator.nickname ?? creator.username}
            </button>
            {"collaborationCount" in creator &&
              creator.isCreator &&
              creator.collaborationCount != null &&
              creator.collaborationCount > 0 && (
                <Tooltip
                  title={`${creator.collaborationCount} previous collaboration${creator.collaborationCount !== 1 ? "s" : ""}`}
                  description={(creator as RecommendedCreator).collaborationNames?.slice(0, 5).join(", ")}
                >
                  <TooltipTrigger>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-success-50 border border-success-200 text-xs font-medium text-success-700 cursor-default">
                      <Target04 className="w-3.5 h-3.5" />
                      {creator.collaborationCount} collaboration{creator.collaborationCount !== 1 ? "s" : ""}
                    </span>
                  </TooltipTrigger>
                </Tooltip>
              )}
          </div>
          <p className="text-sm text-tertiary">{categorySubtitle}</p>
        </div>
      </div>
      {description && <p className="text-sm text-secondary leading-relaxed">{description}</p>}
    </div>
  );
};

const VideoGrid = ({
  videos,
  onVideoClick,
}: {
  videos: {
    id?: string;
    thumbnail?: string | null;
    label1: string;
    value1: string;
    label2: string;
    value2: string;
  }[];
  onVideoClick?: (videoId: string) => void;
}) => (
  <div className="grid grid-cols-3 gap-4">
    {videos.map((v) => (
      <div key={v.id ?? v.thumbnail} className="flex flex-col gap-2">
        {onVideoClick && v.id ? (
          <button
            type="button"
            onClick={() => v.id && onVideoClick(v.id)}
            aria-label={`Open video — ${v.label1}: ${v.value1}, ${v.label2}: ${v.value2}`}
            className="block rounded-lg overflow-hidden bg-bg-quaternary aspect-[9/13] relative group cursor-pointer"
          >
            {v.thumbnail ? (
              <img
                src={v.thumbnail}
                alt=""
                className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
              />
            ) : (
              <div className="w-full h-full bg-bg-quaternary" />
            )}
          </button>
        ) : (
          <div className="block rounded-lg overflow-hidden bg-bg-quaternary aspect-[9/13] relative">
            {v.thumbnail ? (
              <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-bg-quaternary" />
            )}
          </div>
        )}
        <div className="flex justify-between text-xs text-quaternary">
          <div>
            <div>{v.label1}</div>
            <div className="font-medium">{v.value1}</div>
          </div>
          <div className="text-right">
            <div>{v.label2}</div>
            <div className="font-medium">{v.value2}</div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ProfileRightColumn = ({
  topCategories,
  demographicsGender,
  demographicsAge,
  followersLabel,
  avgCommissionRateLabel,
  profilePage,
  currencyFormatter,
  // Candidate classification props
  candidateClassification,
  ecVideoCount,
  gmvAmount,
  // Brand creator classification props
  creatorClassification,
  avgVideos,
  avgGMV,
  videosWithTier1,
  percentageTier2AtRisk,
}: {
  topCategories: string[];
  demographicsGender: { label: string; value: string } | null;
  demographicsAge: { label: string; value: string } | null;
  followersLabel: string;
  avgCommissionRateLabel: string;
  profilePage?: string | null;
  currencyFormatter: Intl.NumberFormat;
  // Candidate classification props
  candidateClassification?: string | null;
  ecVideoCount?: number;
  gmvAmount?: number;
  // Brand creator classification props
  creatorClassification?: string | null;
  avgVideos?: number;
  avgGMV?: number;
  videosWithTier1?: number;
  percentageTier2AtRisk?: number;
}) => {
  const merchant = useMerchant();
  const displayCategories = topCategories.slice(0, 3);
  const categoryCount = Math.min(topCategories.length, 3);

  return (
    <div className="w-[360px] flex-shrink-0 p-8 flex flex-col gap-14">
      {/* Classification card (compact) — candidates */}
      {candidateClassification && ecVideoCount != null && gmvAmount != null && (
        <CandidateClassificationCard
          classification={
            candidateClassification as Parameters<typeof CandidateClassificationCard>[0]["classification"]
          }
          topCategory={displayCategories[0]}
          ecVideoCount={ecVideoCount}
          gmvAmount={gmvAmount}
          currencyFormatter={currencyFormatter}
          compact
        />
      )}

      {/* Classification card (compact) — brand creators */}
      {creatorClassification &&
        avgVideos != null &&
        avgGMV != null &&
        videosWithTier1 != null &&
        percentageTier2AtRisk != null && (
          <ClassificationCard
            classification={creatorClassification as Parameters<typeof ClassificationCard>[0]["classification"]}
            topCategory={displayCategories[0]}
            avgVideos={avgVideos}
            avgGMV={avgGMV}
            videosWithTier1={videosWithTier1}
            percentageTier2AtRisk={percentageTier2AtRisk}
            currencyFormatter={currencyFormatter}
            compact
          />
        )}

      {/* Top selling categories */}
      {displayCategories.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionHeader>
            <Colors className="w-4 h-4" />
            Top {categoryCount} selling {categoryCount === 1 ? "category" : "categories"}
          </SectionHeader>
          <div className="flex flex-wrap gap-2">
            {displayCategories.map((cat) => {
              const isCandidate = !!candidateClassification;
              return merchant ? (
                <Link
                  key={cat}
                  to={isCandidate ? candidateExplorerRoute.to : creatorExplorerRoute.to}
                  params={{ merchantId: merchant }}
                  search={
                    isCandidate
                      ? { topSellingCategories: [{ id: cat, label: cat }] }
                      : { topSellingCategories: [{ id: cat, label: cat }], classification: "__all" }
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-fit"
                  aria-label={`Browse ${isCandidate ? "candidates" : "creators"} in ${cat}`}
                >
                  <Badge color="gray" className="cursor-pointer hover:ring-2 hover:ring-brand-primary transition-all">
                    {cat}
                  </Badge>
                </Link>
              ) : (
                <Badge key={cat} color="gray">
                  {cat}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Demographics */}
      {(demographicsGender || demographicsAge) && (
        <div className="flex flex-col gap-3">
          <SectionHeader>
            <Globe05 className="w-4 h-4" />
            Demographics
          </SectionHeader>
          <div className="flex flex-col">
            {demographicsGender && (
              <div className="flex justify-between py-2 border-b border-border-secondary">
                <span className="text-sm text-secondary capitalize">{demographicsGender.label}</span>
                <span className="text-sm text-secondary">{demographicsGender.value}</span>
              </div>
            )}
            {demographicsAge && (
              <div className="flex justify-between py-2 border-b border-border-secondary">
                <span className="text-sm text-secondary">Age {demographicsAge.label}</span>
                <span className="text-sm text-secondary">{demographicsAge.value}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 py-1 border-b border-border-secondary">
          <Users02 className="w-4 h-4" />
          <span className="text-base font-medium text-primary">{followersLabel}</span>
          <span className="text-sm text-tertiary">Followers</span>
        </div>
        <div className="flex items-center gap-2 py-1 border-b border-border-secondary">
          <BankNote01 className="w-4 h-4" />
          <span className="text-base font-medium text-primary">{avgCommissionRateLabel}</span>
          <span className="text-sm text-tertiary">Average commission rate</span>
        </div>
        {profilePage && (
          <div className="pt-2">
            <a
              href={profilePage}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-secondary hover:text-primary hover:underline flex items-center gap-1.5"
            >
              <svg aria-hidden="true" className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
              See TikTok profile <span className="text-xs">↗</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Brand creator profile (isCreator = true) ─────────────────────────────────

const BrandCreatorProfileInner = ({
  creator,
  onOpenProfile,
  onOpenVideo,
}: {
  creator: UnifiedRecommendedCreator;
  onOpenProfile: (id: string, isCreator?: boolean) => void;
  onOpenVideo: (videoId: string) => void;
}) => {
  const currency = useCurrency();
  const creatorId = "creatorId" in creator ? (creator.creatorId ?? undefined) : undefined;
  const { data, isLoading } = useCreator(creatorId);

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (isLoading) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (!data) return <p className="p-8 text-sm text-tertiary">Creator profile not available.</p>;

  // Top categories — brand creators expose `data.categories` (string[])
  const topCategories = data.categories?.slice(0, 3) ?? [];
  const categorySubtitle = data.categories?.slice(0, 2).join(" | ") ?? creator.username;

  // Demographics: gender is { male?: number, female?: number } as fractions (0–1)
  const genderEntries = data.demographics?.gender
    ? (Object.entries(data.demographics.gender).filter(([, v]) => v != null) as [string, number][])
    : [];
  const topGender = genderEntries.reduce<[string, number] | null>(
    (best, curr) => (!best || curr[1] > best[1] ? curr : best),
    null,
  );

  // Demographics: ageDistribution is Record<string, number> as fractions (0–1)
  const ageEntries = data.demographics?.ageDistribution
    ? (Object.entries(data.demographics.ageDistribution) as [string, number][])
    : [];
  const topAge = ageEntries.reduce<[string, number] | null>(
    (best, curr) => (!best || curr[1] > best[1] ? curr : best),
    null,
  );

  // Videos: urlFirstFrame = thumbnail, gmv + views, videoId for drawer
  const videosForGrid = (data.videos ?? []).slice(0, 6).map((v) => ({
    id: v.videoId,
    thumbnail: v.urlFirstFrame,
    label1: "GMV",
    value1: currencyFormatter.format(v.gmv ?? 0),
    label2: "Views",
    value2: numFormatter.format(v.views ?? 0),
  }));

  return (
    <div className="flex gap-5">
      <div className="flex-1 min-w-0 p-8 flex flex-col gap-10">
        <ProfileHeader
          creator={creator}
          categorySubtitle={categorySubtitle}
          description={data.bioDescription}
          onOpenProfile={onOpenProfile}
        />
        {videosForGrid.length > 0 && <VideoGrid videos={videosForGrid} onVideoClick={onOpenVideo} />}
      </div>
      <ProfileRightColumn
        topCategories={topCategories}
        demographicsGender={topGender ? { label: topGender[0], value: percentFormatter.format(topGender[1]) } : null}
        demographicsAge={topAge ? { label: topAge[0], value: percentFormatter.format(topAge[1]) } : null}
        followersLabel={data.socialStats?.followers ? numFormatter.format(data.socialStats.followers) : "-"}
        avgCommissionRateLabel={
          data.averageCommissionRate != null ? `${Math.round(data.averageCommissionRate * 100)}%` : "-"
        }
        profilePage={data.profilePage}
        currencyFormatter={currencyFormatter}
        creatorClassification={data.classification}
        avgVideos={data.avgVideos}
        avgGMV={data.avgGMV}
        videosWithTier1={data.safetyAnalysis?.videosWithTier1}
        percentageTier2AtRisk={data.safetyAnalysis?.percentageTier2AtRisk}
      />
    </div>
  );
};

// ─── Candidate profile (isCreator = false) ────────────────────────────────────

const CandidateProfileInner = ({
  creator,
  onOpenProfile,
}: {
  creator: UnifiedRecommendedCreator;
  onOpenProfile: (id: string, isCreator?: boolean) => void;
}) => {
  const currency = useCurrency();
  const { data, isLoading } = useCandidate(creator.tiktokCreatorOpenId);

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (isLoading) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (!data) return <p className="p-8 text-sm text-tertiary">Candidate profile not available.</p>;

  // Top categories — prefer GMV-ranked, fall back to raw categories[]
  const topCategories =
    data.categoryGmvDistribution && data.categoryGmvDistribution.length > 0
      ? data.categoryGmvDistribution
          .filter((c) => c.category !== "-1")
          .slice(0, 3)
          .map((c) => c.category)
      : (data.categories?.slice(0, 3) ?? []);

  const categorySubtitle = data.categories?.slice(0, 2).join(" | ") ?? creator.username;

  // Demographics: followerGenderDistribution[].percentage is a string fraction "0.78"
  const topGender = data.followerGenderDistribution?.reduce(
    (max, curr) => (Number.parseFloat(curr.percentage) > Number.parseFloat(max.percentage) ? curr : max),
    data.followerGenderDistribution[0],
  );
  // Demographics: followerAgeDistribution[].age is the label, .percentage is a string fraction
  const topAgeRange = data.followerAgeDistribution?.reduce(
    (max, curr) => (Number.parseFloat(curr.percentage) > Number.parseFloat(max.percentage) ? curr : max),
    data.followerAgeDistribution[0],
  );

  // Videos: from aiFeatures.videos — thumbnail, viewCount, likeCount (not clickable for candidates)
  const videosForGrid = (data.aiFeatures?.videos ?? []).slice(0, 6).map((v) => ({
    thumbnail: v.thumbnail,
    label1: "Views",
    value1: numFormatter.format(v.viewCount),
    label2: "Likes",
    value2: numFormatter.format(v.likeCount),
  }));

  return (
    <div className="flex gap-5">
      <div className="flex-1 min-w-0 p-8 flex flex-col gap-10">
        <ProfileHeader
          creator={creator}
          categorySubtitle={categorySubtitle}
          description={data.aiFeatures?.descriptiveSummaryOfCreator}
          onOpenProfile={onOpenProfile}
        />
        {videosForGrid.length > 0 && <VideoGrid videos={videosForGrid} />}
      </div>
      <ProfileRightColumn
        topCategories={topCategories}
        demographicsGender={
          topGender
            ? {
                label: topGender.gender,
                value: percentFormatter.format(Number.parseFloat(topGender.percentage)),
              }
            : null
        }
        demographicsAge={
          topAgeRange
            ? {
                label: topAgeRange.age,
                value: percentFormatter.format(Number.parseFloat(topAgeRange.percentage)),
              }
            : null
        }
        followersLabel={data.followerCount ? numFormatter.format(data.followerCount) : "-"}
        avgCommissionRateLabel={data.avgCommissionRate != null ? `${Math.round(data.avgCommissionRate * 100)}%` : "-"}
        profilePage={data.profilePage}
        candidateClassification={data.classification}
        ecVideoCount={data.ecVideoCount ?? 0}
        gmvAmount={data.gmvAmount ?? 0}
        currencyFormatter={currencyFormatter}
      />
    </div>
  );
};

// ─── Dispatcher: picks the right inner panel based on isCreator ───────────────

const CreatorProfilePanel = ({
  creator,
  onOpenProfile,
  onOpenVideo,
}: {
  creator: UnifiedRecommendedCreator;
  onOpenProfile: (id: string, isCreator?: boolean) => void;
  onOpenVideo: (videoId: string) => void;
}) => {
  const isCreator = "isCreator" in creator ? creator.isCreator : false;

  return isCreator ? (
    <BrandCreatorProfileInner creator={creator} onOpenProfile={onOpenProfile} onOpenVideo={onOpenVideo} />
  ) : (
    <CandidateProfileInner creator={creator} onOpenProfile={onOpenProfile} />
  );
};

// ─── Main view ────────────────────────────────────────────────────────────────

type RecommendedCreatorsViewProps = {
  mode?: "collaboration" | "explorer";
  explorerType?: "creator" | "candidate";
  filters?: RecommendationsFilters;
  isFiltered?: boolean;
  filterControls?: ReactNode;
};

export const RecommendedCreatorsView = ({
  mode: propMode,
  explorerType: propExplorerType,
  filters,
  isFiltered = false,
  filterControls,
}: RecommendedCreatorsViewProps = {}) => {
  const params = useParams({ strict: false }) as { collaborationId?: string };
  const searchParams = useSearch({ strict: false }) as { type?: string; groupId?: string };
  const navigate = useNavigate();
  const merchant = useMerchant();

  // Determine mode: collaboration (has collaborationId) or explorer (no collaborationId)
  const collaborationId = params.collaborationId;
  const isCollaborationMode = propMode === "collaboration" || !!collaborationId;

  // Smart Group: when groupId is present, invite goes to all collabs in the group
  const groupId = searchParams.groupId;
  const { data: smartGroup } = useTargetCollaborationGroupDetail(isCollaborationMode ? groupId : undefined);
  const smartGroupCollaborationIds = smartGroup?.collaborationIds ?? null;
  const explorerType = propExplorerType ?? (searchParams.type === "candidate" ? "candidate" : "creator");
  const isCreatorFilter = explorerType === "creator";

  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showRemoveFromCollabModal, setShowRemoveFromCollabModal] = useState(false);
  const [profileDrawer, setProfileDrawer] = useState<{ id: string; isCreator: boolean } | undefined>(undefined);
  const [videoDrawerId, setVideoDrawerId] = useState<string | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<SortColumn>("score");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [addToListsOpen, setAddToListsOpen] = useState(false);

  // Collaboration mode hooks
  const { data: collaboration, isLoading: collabLoading } = useTargetCollaborationDetail(
    isCollaborationMode ? collaborationId : undefined,
  );

  const {
    data: collabRecommendationsData,
    isLoading: collabRecsLoading,
    isFetching: collabIsFetching,
    refetch: refetchCollabRecs,
  } = useCollaborationRecommendations(isCollaborationMode ? collaborationId : undefined, {
    pagination: { limit: BATCH_SIZE, offset: page * BATCH_SIZE },
  });

  // Explorer mode hooks
  const {
    data: merchantRecommendationsData,
    isLoading: merchantRecsLoading,
    isFetching: merchantIsFetching,
    refetch: refetchMerchantRecs,
  } = useMerchantRecommendations({
    pagination: { limit: BATCH_SIZE, offset: page * BATCH_SIZE },
    isCreator: isCreatorFilter,
    enabled: !isCollaborationMode,
    filters,
  });

  // Use appropriate data based on mode
  const recommendationsData = isCollaborationMode ? collabRecommendationsData : merchantRecommendationsData;
  const recsLoading = isCollaborationMode ? collabRecsLoading : merchantRecsLoading;
  const isFetching = isCollaborationMode ? collabIsFetching : merchantIsFetching;
  const refetchRecommendations = isCollaborationMode ? refetchCollabRecs : refetchMerchantRecs;

  // Collaboration mode mutations
  const { mutateAsync: passOnCreatorCollab, isPending: isPassingCollab } = usePassRecommendation(collaborationId ?? "");
  const { mutateAsync: recordFetchCollab } = useRecordFetchAction(collaborationId ?? "");

  // After a successful bulk-add-creators, the server records `send_creator_invite` NBA
  // interactions itself (via the workflow / sync route). The FE no longer fires per-creator
  // POSTs — instead we just keep the local cache in sync so the list/count update instantly.
  const optimisticallyRemoveFromCollab = useOptimisticallyRemoveCollaborationRecommendations(collaborationId);

  // Smart groups still rely on the per-sibling POST path: the bulk-add-creators API targets one
  // collab, but a smart-group invite needs analytics recorded against every sibling collab in
  // the group. These don't publish drafts, so the race the workflow-recording fixes doesn't
  // apply here.
  const { mutateAsync: recordInviteForGroup } = useRecordInviteActionForGroup(
    isCollaborationMode ? (smartGroupCollaborationIds ?? []) : [],
  );

  // Explorer mode mutations - pass isCreatorFilter to scope cache updates to the active tab
  const { mutateAsync: passOnCreatorMerchant, isPending: isPassingMerchant } =
    usePassMerchantRecommendation(isCreatorFilter);
  const { mutateAsync: recordInviteMerchant } = useRecordMerchantInviteAction(isCreatorFilter);

  // Use appropriate mutations based on mode
  const passOnCreator = isCollaborationMode ? passOnCreatorCollab : passOnCreatorMerchant;
  const isPassing = isCollaborationMode ? isPassingCollab : isPassingMerchant;

  const recommendations: UnifiedRecommendedCreator[] = recommendationsData?.recommendations ?? [];
  const total = recommendationsData?.total ?? 0;
  const hasMoreRecommendations = recommendationsData?.hasUnactionedRecommendations ?? false;

  const sortedRecommendations = useMemo(() => {
    return [...recommendations].sort((a, b) => {
      if (sortColumn === "name") {
        const aName = (a.nickname ?? a.username).toLowerCase();
        const bName = (b.nickname ?? b.username).toLowerCase();
        return sortDirection === "asc" ? aName.localeCompare(bName) : bName.localeCompare(aName);
      }
      return sortDirection === "asc"
        ? a.matchQualityScore - b.matchQualityScore
        : b.matchQualityScore - a.matchQualityScore;
    });
  }, [recommendations, sortColumn, sortDirection]);

  const handleSortToggle = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortDirection(col === "score" ? "desc" : "asc");
    }
  };

  const {
    selectedKeys,
    selectedData,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<UnifiedRecommendedCreator, UnifiedRecommendedCreator>({
    getKey: useCallback((c: UnifiedRecommendedCreator) => c.tiktokCreatorOpenId, []),
    toData: useCallback((c: UnifiedRecommendedCreator) => c, []),
  });
  const checkedCreators = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const handleSelectionChange = useMemo(
    () => createSelectionHandler(sortedRecommendations),
    [createSelectionHandler, sortedRecommendations],
  );

  const fetchCollaborationRows = useCollaborationRecommendationRowsFetcher(
    isCollaborationMode ? collaborationId : undefined,
  );
  const fetchMerchantRows = useMerchantRecommendationRowsFetcher({ isCreator: isCreatorFilter, filters });
  const fetchChunk = useCallback(
    (offset: number, limit: number): Promise<ChunkResponse<UnifiedRecommendedCreator>> =>
      isCollaborationMode ? fetchCollaborationRows(offset, limit) : fetchMerchantRows(offset, limit),
    [isCollaborationMode, fetchCollaborationRows, fetchMerchantRows],
  );
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total,
    loadedCount: recommendations.length,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify({ isCollaborationMode, collaborationId, isCreatorFilter, filters }),
  });

  const allChecked =
    sortedRecommendations.length > 0 && sortedRecommendations.every((c) => checkedCreators.has(c.tiktokCreatorOpenId));
  const _someChecked = !allChecked && sortedRecommendations.some((c) => checkedCreators.has(c.tiktokCreatorOpenId));

  const handleToggleAll = () => handleSelectionChange(allChecked ? new Set() : "all");

  const handleToggleCreator = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const checkedOnPage = new Set(
      sortedRecommendations.map((c) => c.tiktokCreatorOpenId).filter((key) => checkedCreators.has(key)),
    );
    if (checkedOnPage.has(id)) checkedOnPage.delete(id);
    else checkedOnPage.add(id);
    handleSelectionChange(checkedOnPage);
  };

  // Derive the current position from the selected creator's ID so re-sorting always
  // reflects the correct "N of total" number in pagination.
  const selectedIndex = useMemo(() => {
    if (!selectedCreatorId) return 0;
    const idx = sortedRecommendations.findIndex((c) => c.tiktokCreatorOpenId === selectedCreatorId);
    return idx >= 0 ? idx : 0;
  }, [selectedCreatorId, sortedRecommendations]);

  const selectedCreator = sortedRecommendations[selectedIndex] ?? null;
  const globalIndex = page * BATCH_SIZE + selectedIndex;

  // Fetch full data for the selected brand creator (cache hit when CreatorProfilePanel is already showing them)
  const selectedBrandCreatorId = selectedCreator?.isCreator ? (selectedCreator.creatorId ?? undefined) : undefined;
  const { data: selectedCreatorFullData } = useCreator(selectedBrandCreatorId);

  // Reset selection when page changes (new set of creators loaded)
  useEffect(() => {
    setSelectedCreatorId(null);
  }, [page]);

  useEffect(() => {
    setPage(0);
    setSelectedCreatorId(null);
    clearSelection();
  }, [filters, clearSelection]);

  // Record that the user was shown this recommendation (fetched_at).
  // Fire-and-forget: don't block the UI and don't retry on failure.
  useEffect(() => {
    if (!selectedCreator || !isCollaborationMode) return;
    recordFetchCollab({
      tiktokCreatorOpenId: selectedCreator.tiktokCreatorOpenId,
      modelVersion: selectedCreator.modelVersion,
      action: selectedCreator.action,
    }).catch(() => {
      // Silently ignore — fetch tracking is best-effort
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreator?.tiktokCreatorOpenId]);

  const handlePrevious = useCallback(() => {
    if (selectedIndex > 0) {
      setSelectedCreatorId(sortedRecommendations[selectedIndex - 1].tiktokCreatorOpenId);
    } else if (page > 0) {
      setPage((p) => p - 1);
      // selection resets to first on new page via the page-change effect
    }
  }, [selectedIndex, sortedRecommendations, page]);

  const handleNext = useCallback(() => {
    if (selectedIndex < sortedRecommendations.length - 1) {
      setSelectedCreatorId(sortedRecommendations[selectedIndex + 1].tiktokCreatorOpenId);
    } else if ((page + 1) * BATCH_SIZE < total) {
      setPage((p) => p + 1);
      // selection resets to first on new page via the page-change effect
    }
  }, [selectedIndex, sortedRecommendations, page, total]);

  const handlePass = useCallback(async () => {
    if (!selectedCreator) return;
    await passOnCreator({
      tiktokCreatorOpenId: selectedCreator.tiktokCreatorOpenId,
      modelVersion: selectedCreator.modelVersion,
      action: selectedCreator.action,
    });
    success(`Passed on ${selectedCreator.nickname ?? selectedCreator.username}`);
  }, [selectedCreator, passOnCreator]);

  const handleBulkPass = useCallback(async () => {
    const targets = [...selectedData.values()];
    if (targets.length === 0) return;
    await runInBatches(targets, PASS_CONCURRENCY, (c) =>
      passOnCreator({
        tiktokCreatorOpenId: c.tiktokCreatorOpenId,
        modelVersion: c.modelVersion,
        action: c.action,
      }),
    );
    selectAll.clear();
    success(`Ignored ${targets.length} recommendation${targets.length !== 1 ? "s" : ""}`);
  }, [selectedData, passOnCreator, selectAll.clear]);

  // Creators targeted by the next modal action:
  //   • If any rows are checked → use those (bulk action)
  //   • Otherwise → use the currently selected creator (single action from bottom bar)
  const targetCreators = useMemo(() => {
    if (selectedCount > 0) return [...selectedData.values()];
    return selectedCreator ? [selectedCreator] : [];
  }, [selectedCount, selectedData, selectedCreator]);

  const removeFromCollabCreatorIds = useMemo(() => {
    const sources = selectedCount > 0 ? targetCreators : selectedCreator ? [selectedCreator] : [];
    return sources.filter((c) => c.isCreator && c.creatorId && c.collaborationCount).map((c) => c.creatorId as string);
  }, [selectedCount, targetCreators, selectedCreator]);

  const handleInviteSuccess = useCallback(
    async (_publishedCollaborationId?: string) => {
      if (targetCreators.length === 0) return;
      // Smart group still records per sibling collab via the legacy endpoint (workflow recorder
      // only covers the bulk-add target). Single-collab path leaves NBA recording to the server —
      // bulk-add-creators handles it via the workflow / sync route now.
      if (smartGroupCollaborationIds) {
        await runInBatches(targetCreators, PASS_CONCURRENCY, (c) =>
          recordInviteForGroup({
            tiktokCreatorOpenId: c.tiktokCreatorOpenId,
            modelVersion: c.modelVersion,
            action: c.action,
          }),
        );
      } else {
        optimisticallyRemoveFromCollab(targetCreators.map((c) => c.tiktokCreatorOpenId));
      }
      setShowInviteModal(false);
      selectAll.clear();
      const firstName = targetCreators[0].nickname ?? targetCreators[0].username;
      success(
        targetCreators.length === 1
          ? `Invited ${firstName} to collaboration`
          : `Invited ${targetCreators.length} creators to collaboration`,
      );
    },
    [targetCreators, smartGroupCollaborationIds, recordInviteForGroup, optimisticallyRemoveFromCollab, selectAll.clear],
  );

  const canGoPrevious = globalIndex > 0;
  const canGoNext = globalIndex < total - 1;

  const handleQuickInviteSuccess = useCallback(
    async (_publishedCollaborationId?: string) => {
      if (targetCreators.length === 0) return;

      if (smartGroupCollaborationIds) {
        await runInBatches(targetCreators, PASS_CONCURRENCY, (c) =>
          recordInviteForGroup({
            tiktokCreatorOpenId: c.tiktokCreatorOpenId,
            modelVersion: c.modelVersion,
            action: c.action,
          }),
        );
      } else {
        optimisticallyRemoveFromCollab(targetCreators.map((c) => c.tiktokCreatorOpenId));
      }

      setShowInviteModal(false);

      // If bulk selection, clear checkboxes. If single selection, move to next.
      if (selectedCount > 0) {
        selectAll.clear();
      } else if (canGoNext) {
        handleNext();
      }
    },
    [
      targetCreators,
      smartGroupCollaborationIds,
      recordInviteForGroup,
      optimisticallyRemoveFromCollab,
      selectedCount,
      selectAll.clear,
      canGoNext,
      handleNext,
    ],
  );
  const isLoading = (isCollaborationMode && collabLoading) || recsLoading;

  // Back navigation helper
  const handleBackNavigation = useCallback(() => {
    if (isCollaborationMode && groupId) {
      // Came from a Smart Group — return to the group detail page
      navigate({
        to: "/merchant/$merchantId/collaborations/target-group/$groupId",
        params: { merchantId: merchant, groupId },
      });
    } else if (isCollaborationMode && collaborationId) {
      navigate({
        to: "/merchant/$merchantId/collaborations/target/$collaborationId",
        params: { merchantId: merchant, collaborationId },
      });
    } else {
      navigate({
        to: isCreatorFilter ? "/merchant/$merchantId/creator-explorer" : "/merchant/$merchantId/candidate-explorer",
        params: { merchantId: merchant },
      });
    }
  }, [isCollaborationMode, groupId, collaborationId, isCreatorFilter, merchant, navigate]);

  const backLabel = isCollaborationMode
    ? groupId
      ? `Back to ${smartGroup?.name ?? "Smart Group"}`
      : `Back to ${collaboration?.name ?? "collaboration"}`
    : isCreatorFilter
      ? "Back to All Creators"
      : "Back to All Candidates";

  const pageTitle = "Review Matches";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingIndicator size="lg" />
      </div>
    );
  }

  // Only show "Collaboration not found" in collaboration mode
  if (isCollaborationMode && !collaboration) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <EmptyState size="md">
          <div className="pb-6">
            <EmptyState.FeaturedIcon color="gray" />
          </div>
          <EmptyState.Content>
            <EmptyState.Title>Collaboration not found</EmptyState.Title>
          </EmptyState.Content>
          <EmptyState.Footer>
            <Button
              color="secondary"
              size="md"
              onClick={() => navigate({ to: "/merchant/$merchantId/collaborations", params: { merchantId: merchant } })}
              iconLeading={ChevronLeft}
            >
              Back to Collaborations
            </Button>
          </EmptyState.Footer>
        </EmptyState>
      </div>
    );
  }

  if (recommendations.length === 0 && !isFetching) {
    return (
      <div className="flex w-full h-full justify-center">
        <div className={cx("flex w-full h-full overflow-hidden px-16 pb-6 gap-6", LAYOUT_CONSTRAINTS.CONTAINER_WIDTH)}>
          {/* Left column */}
          <div className="w-[420px] flex flex-col flex-shrink-0 pt-8">
            <Button
              color="tertiary"
              onClick={handleBackNavigation}
              className="text-quaternary hover:text-primary pl-0 w-fit mb-2"
            >
              <div className="flex items-center">
                <ChevronLeft className="w-4 h-4 mr-1" />
                {backLabel}
              </div>
            </Button>
            <div className={cx("flex-shrink-0", filterControls ? "pb-4" : "pb-8")}>
              <div className="flex items-center gap-2.5">
                <UserSquare className="w-5 h-5 text-fg-secondary flex-shrink-0" />
                <h1 className="text-xl font-normal text-primary">{pageTitle}</h1>
              </div>
            </div>
            {filterControls && <div className="flex flex-col gap-3 pb-4 flex-shrink-0">{filterControls}</div>}
            <div className="flex-1 rounded-xl bg-bg-secondary" />
          </div>

          {/* Right column */}
          <div className="flex-1 rounded-xl bg-bg-secondary flex flex-col items-center justify-center gap-4 min-w-[700px] mt-8">
            {isFiltered ? (
              <>
                <div className="w-12 h-12 rounded-full bg-bg-tertiary flex items-center justify-center">
                  <SearchMd className="w-6 h-6 text-fg-quaternary" />
                </div>
                <div className="text-center flex flex-col gap-1">
                  <p className="text-sm font-semibold text-primary">No matches found</p>
                  <p className="text-sm text-tertiary max-w-xs">Try a different search or clear your filters</p>
                </div>
              </>
            ) : hasMoreRecommendations ? (
              <>
                <div className="w-12 h-12 rounded-full bg-bg-tertiary flex items-center justify-center">
                  <Eye className="w-6 h-6 text-fg-quaternary" />
                </div>
                <div className="text-center flex flex-col gap-1">
                  <p className="text-sm font-semibold text-primary">Looking for more?</p>
                  <p className="text-sm text-tertiary max-w-xs">
                    Explore creators beyond our top tier,
                    <br />
                    you might find a great fit
                  </p>
                </div>
                <Button color="primary" size="md" iconLeading={RefreshCcw05} onClick={() => refetchRecommendations()}>
                  Load more recommendations
                </Button>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-bg-tertiary flex items-center justify-center">
                  <UserSquare className="w-6 h-6 text-fg-quaternary" />
                </div>
                <div className="text-center flex flex-col gap-1">
                  <p className="text-sm font-semibold text-primary">All recommended creators have been reviewed</p>
                </div>
                <Button color="primary" size="md" iconLeading={ChevronLeft} onClick={handleBackNavigation}>
                  {backLabel}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full h-full justify-center">
      <div className={cx("flex w-full h-full overflow-hidden px-16 pb-6 gap-6", LAYOUT_CONSTRAINTS.CONTAINER_WIDTH)}>
        {/* Left column */}
        <div className="w-[420px] flex flex-col flex-shrink-0 pt-8">
          <Button
            color="tertiary"
            onClick={handleBackNavigation}
            className="text-quaternary hover:text-primary pl-0 w-fit mb-2"
          >
            <div className="flex items-center">
              <ChevronLeft className="w-4 h-4 mr-1" />
              {backLabel}
            </div>
          </Button>
          <div className="pb-4 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <UserSquare className="w-5 h-5 text-fg-secondary flex-shrink-0" />
              <h1 className="text-xl font-normal text-primary">{pageTitle}</h1>
            </div>
          </div>
          {filterControls && <div className="flex flex-col gap-3 pb-4 flex-shrink-0">{filterControls}</div>}
          {/* Scrollable table with gradient overlay */}
          <div className="flex-1 overflow-hidden relative">
            <div className="absolute inset-0 overflow-y-auto scrollbar-recs">
              <table className="w-full table-fixed">
                {/* colgroup locks column widths so the table never overflows */}
                <colgroup>
                  <col className="w-11" />
                  <col />
                  <col className="w-[152px]" />
                </colgroup>

                <thead className="sticky top-0 z-10">
                  {selectedCount > 0 ? (
                    /* Bulk-action header — replaces normal header when rows are selected */
                    <tr>
                      <th colSpan={3} className="p-0">
                        <div className="flex flex-row justify-between items-center bg-brand-primary px-3 py-2 border-b border-brand-300">
                          <div className="flex flex-row items-center gap-2">
                            <CloseButton onClick={selectAll.clear} />
                            <span className="text-sm">{selectedCount.toLocaleString()} Selected</span>
                            <SelectAllPagesButton state={selectAll} />
                          </div>
                          <div className="flex flex-row gap-1 items-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild disabled={selectAll.isFetching}>
                                <Button
                                  color="secondary"
                                  size="sm"
                                  iconLeading={DotsVertical}
                                  isDisabled={selectAll.isFetching}
                                />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setShowMessageModal(true)}>
                                  <MessageTextSquare02 className="mr-2 size-4" />
                                  Message Creator on TikTok
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setAddToListsOpen(true)}>
                                  <FilePlus02 className="mr-2 size-4" />
                                  Add to List
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            <Tooltip title="Ignore recommendation">
                              <Button
                                color="primary"
                                size="sm"
                                iconLeading={UserX01}
                                onClick={handleBulkPass}
                                isDisabled={isPassing || selectAll.isFetching}
                                className="*:data-icon:!text-white hover:*:data-icon:!text-white"
                              />
                            </Tooltip>

                            <Tooltip title="Invite to collaborate">
                              <Button
                                color="primary"
                                size="sm"
                                iconLeading={Announcement03}
                                onClick={() => setShowInviteModal(true)}
                                isDisabled={selectAll.isFetching}
                                className="*:data-icon:!text-white hover:*:data-icon:!text-white"
                              />
                            </Tooltip>
                          </div>
                        </div>
                      </th>
                    </tr>
                  ) : (
                    /* Normal column headers */
                    <tr className="bg-[#FAFAFA]">
                      <th className="py-3 pl-4 pr-2 text-left align-middle border-b border-gray-200">
                        <button type="button" onClick={handleToggleAll} className="cursor-pointer">
                          <CheckboxBase isSelected={allChecked} isIndeterminate={_someChecked} />
                        </button>
                      </th>
                      <th className="py-3 px-2 text-left align-middle border-b border-gray-200">
                        <button
                          type="button"
                          onClick={() => handleSortToggle("name")}
                          className="flex items-center gap-1.5 text-xs font-medium text-quaternary tracking-wide hover:text-fg-secondary transition-colors"
                        >
                          {compactNumberFormatter.format(total)} {isCreatorFilter ? "Creators" : "Candidates"}
                          <SortIcon active={sortColumn === "name"} direction={sortDirection} />
                        </button>
                      </th>
                      <th className="py-3 pl-2 pr-4 text-left align-middle border-b border-gray-200">
                        <button
                          type="button"
                          onClick={() => handleSortToggle("score")}
                          className="flex items-center gap-1.5 text-xs font-medium text-quaternary tracking-wide hover:text-fg-secondary transition-colors"
                        >
                          Match Quality
                          <SortIcon active={sortColumn === "score"} direction={sortDirection} />
                        </button>
                      </th>
                    </tr>
                  )}
                </thead>

                <tbody>
                  {sortedRecommendations.map((creator) => {
                    const isChecked = checkedCreators.has(creator.tiktokCreatorOpenId);
                    const isSelected = creator.tiktokCreatorOpenId === selectedCreator?.tiktokCreatorOpenId;
                    return (
                      <tr
                        key={creator.tiktokCreatorOpenId}
                        className={cx(
                          "cursor-pointer transition-colors",
                          isChecked
                            ? "bg-brand-100 hover:bg-brand-50"
                            : isSelected
                              ? "bg-[#FAFAFA] hover:bg-gray-100"
                              : "bg-white hover:bg-gray-50",
                        )}
                        onClick={() => setSelectedCreatorId(creator.tiktokCreatorOpenId)}
                      >
                        <td
                          className="py-3 pl-4 pr-2 align-middle border-b border-gray-100"
                          onClick={(e) => handleToggleCreator(creator.tiktokCreatorOpenId, e)}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter")
                              handleToggleCreator(creator.tiktokCreatorOpenId, e as never);
                          }}
                        >
                          <CheckboxBase isSelected={checkedCreators.has(creator.tiktokCreatorOpenId)} />
                        </td>
                        <td className="py-3 px-2 align-middle border-b border-gray-100 min-w-0">
                          <div className="flex items-center gap-2.5 overflow-hidden">
                            {creator.avatar ? (
                              <img
                                src={creator.avatar}
                                alt={creator.nickname ?? creator.username}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-bg-quaternary flex items-center justify-center flex-shrink-0">
                                <User01 className="w-4 h-4 text-fg-quaternary" />
                              </div>
                            )}
                            <span className="text-sm font-medium text-primary truncate">
                              {creator.nickname ?? creator.username}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 pl-2 pr-4 align-middle border-b border-gray-100">
                          <Tooltip
                            title="This creator's match score combines two factors"
                            description={
                              <div className="flex flex-col gap-3 text-left">
                                <div>
                                  <div className="font-medium mb-1">Revenue Potential</div>
                                  <ul className="list-disc list-inside text-xs">
                                    <li>
                                      Our AI analyses all creators and brand features (posting frequency, historical
                                      performance, audience demographics, etc.) to predict how much revenue they could
                                      generate for your brand.
                                    </li>
                                  </ul>
                                </div>
                                <div>
                                  <div className="font-medium mb-1">Brand Fit</div>
                                  <ul className="list-disc list-inside text-xs">
                                    <li>
                                      We're comparing your brand vertical with the creators primary content category to
                                      identify alignment.
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            }
                          >
                            <TooltipTrigger>
                              <div className="flex items-center gap-2 cursor-help">
                                <span className="text-sm font-medium text-secondary w-9 shrink-0 text-right tabular-nums">
                                  {creator.matchQualityScore}%
                                </span>
                                <ProgressBarBase
                                  value={creator.matchQualityScore}
                                  className="w-20 h-1.5"
                                  progressClassName="bg-brand-900"
                                />
                              </div>
                            </TooltipTrigger>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Bottom gradient fade — sits outside the scroll container so it never scrolls away */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none z-20" />
          </div>
        </div>

        {/* Right column */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-[700px] pt-4">
          {/* Gray rounded panel — scrollable */}
          <div className="flex-1 overflow-y-auto scrollbar-stable bg-bg-secondary rounded-xl">
            {selectedCreator ? (
              <CreatorProfilePanel
                creator={selectedCreator}
                onOpenProfile={(id, isCreator) => setProfileDrawer({ id, isCreator: isCreator ?? false })}
                onOpenVideo={setVideoDrawerId}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-tertiary text-sm">
                Select a creator from the list
              </div>
            )}
          </div>

          {/* Bottom action bar — outside and below the gray panel */}
          <div className="py-4 flex-shrink-0">
            <div className="flex items-center flex-nowrap">
              {/* Previous arrow - left side */}
              <button
                type="button"
                onClick={handlePrevious}
                disabled={!canGoPrevious || isPassing}
                className="w-10 h-10 flex items-center justify-center border border-border-secondary rounded-lg hover:bg-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous"
              >
                <ArrowLeft className="w-5 h-5 text-fg-secondary" />
              </button>

              {/* Count - centered with flex-1 and fixed min-width to prevent layout shifts */}
              <div className="flex-1 flex justify-center">
                <span className="tabular-nums min-w-[8ch] text-center text-sm text-secondary">
                  {globalIndex + 1} of {total}
                </span>
              </div>

              {/* Next arrow - right side */}
              <button
                type="button"
                onClick={handleNext}
                disabled={!canGoNext || isPassing}
                className="w-10 h-10 flex items-center justify-center border border-border-secondary rounded-lg hover:bg-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Next"
              >
                <ArrowRight className="w-5 h-5 text-fg-secondary" />
              </button>

              {/* Actions - right side after next arrow */}
              <div className="flex items-center gap-3 ml-3">
                {/* 3-dot menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={!selectedCreator || selectedCount > 0}>
                    <button
                      type="button"
                      className="flex items-center justify-center w-10 h-10 border border-border-secondary rounded-lg hover:bg-bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="More actions"
                      disabled={!selectedCreator || selectedCount > 0}
                    >
                      <DotsVertical className="w-5 h-5 text-fg-tertiary" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowMessageModal(true)}>
                      <MessageTextSquare02 className="mr-2 size-4" />
                      Message Creator on TikTok
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setAddToListsOpen(true)}>
                      <FilePlus02 className="mr-2 size-4" />
                      Add to List
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowRemoveFromCollabModal(true)}
                      disabled={removeFromCollabCreatorIds.length === 0}
                    >
                      <XCircle className="mr-2 size-4" />
                      Remove from collaboration
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  color="secondary"
                  size="md"
                  iconLeading={UserX01}
                  onClick={handlePass}
                  isDisabled={isPassing || !selectedCreator || selectedCount > 0}
                >
                  Pass on creator
                </Button>
                <Button
                  color="primary"
                  size="md"
                  iconLeading={Announcement03}
                  onClick={() => setShowInviteModal(true)}
                  isDisabled={isPassing || !selectedCreator || selectedCount > 0}
                >
                  Invite to collaboration
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Modals — driven by targetCreators (bulk checked or single selected) */}
        {targetCreators.length > 0 &&
          (isCollaborationMode && collaborationId && shouldShowQuickInviteModal() ? (
            <QuickInviteModal
              creators={targetCreators.map((c) => ({
                openId: c.tiktokCreatorOpenId,
                username: c.username,
                nickname: c.nickname,
                avatar: c.avatar,
              }))}
              collaborationId={collaborationId}
              collaborationName={collaboration?.name ?? "Collaboration"}
              open={showInviteModal}
              onOpenChange={setShowInviteModal}
              onSuccess={handleQuickInviteSuccess}
              nbaModelVersion={targetCreators[0]?.modelVersion}
            />
          ) : (
            <InviteToCollaborationModal
              creators={targetCreators.map((c) => ({
                openId: c.tiktokCreatorOpenId,
                username: c.username,
                nickname: c.nickname,
                avatar: c.avatar,
              }))}
              open={showInviteModal}
              onOpenChange={setShowInviteModal}
              onSuccess={handleInviteSuccess}
              preselectedCollaborationId={collaborationId}
              nbaModelVersion={targetCreators[0]?.modelVersion}
            />
          ))}

        {selectedCreatorFullData && (
          <RemoveCreatorFromCollabModal
            creator={selectedCreatorFullData}
            open={showRemoveFromCollabModal}
            onOpenChange={setShowRemoveFromCollabModal}
          />
        )}

        {targetCreators.length > 0 && showMessageModal && (
          <MessageCreatorsDialog
            recipients={targetCreators.map((c) => ({
              creatorId: c.tiktokCreatorOpenId,
              handle: c.nickname ?? c.username ?? null,
              thumbnail: c.avatar,
            }))}
            open={showMessageModal}
            onOpenChange={(open) => {
              if (!open) setShowMessageModal(false);
            }}
          />
        )}

        <AddtoListsModal
          selectedCreators={targetCreators.map((c) => c.tiktokCreatorOpenId)}
          onAddToList={() => setAddToListsOpen(false)}
          open={addToListsOpen}
          onOpenChange={setAddToListsOpen}
        />

        {/* Profile drawer — opened by clicking creator name */}
        {profileDrawer?.isCreator ? (
          <CreatorDetails id={profileDrawer.id} onClose={() => setProfileDrawer(undefined)} />
        ) : (
          <CandidateDetails id={profileDrawer?.id} onClose={() => setProfileDrawer(undefined)} />
        )}

        {/* Video drawer — opened by clicking a video (brand creators only) */}
        <VideoDetails id={videoDrawerId} onClose={() => setVideoDrawerId(undefined)} />
      </div>
    </div>
  );
};
